/* eslint-disable camelcase */

import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import Account from "byzantine/src/Account";
import {
  loanPaymentForm,
  loanPaymentFormInitialValues,
  useLoanPaymentEnabled,
} from "byzantine/src/dbbl/businessLogic/modules/loanPayment";
import AccountContext from "../../contexts/AccountContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import useActions from "../../hooks/useActions";
import LoanPaymentFormComponent from "./unified/LoanPaymentForm";
import LoanPaymentByCard from "./payment_by_card/LoanPaymentByCard";
import LoanPaydown from "./paydown/LoanPaydown";
import LoanEmptyState from "./LoanEmptyState";

interface LoanPaymentRouterProps {
  limits: object;
  legacy?: boolean;
}

interface UserFeatures {
  loan_payment_by_card: boolean;
  loan_principal_payment: boolean;
}

const { Provider: LoanPaymentFormProvider } = loanPaymentForm;

const LoanPaymentRouter = ({ limits }: LoanPaymentRouterProps) => {
  const { accounts } = useContext(AccountContext) as { accounts: Account[] };
  const { ach_allows_pull = false } = useInstitutionSettings();
  const { loan_payment_by_card, loan_principal_payment } =
    useUserFeatures() as UserFeatures;
  const { isWebLoanPaymentsEnabled } = useLoanPaymentEnabled();

  const [toAccountId, setToAccountId] = useState<API.AccountId>();
  const [isInitialized, setIsInitialized] = useState(false);
  const { goToPrevious } = useActions();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      !(
        isWebLoanPaymentsEnabled ||
        loan_payment_by_card ||
        loan_principal_payment
      )
    ) {
      navigate(`/transfer/funds?${params.toString()}`, { replace: true });
      return;
    }

    const loan_id = params.get("to_account_id") as API.AccountId;
    const loan = accounts.find((account) => account.id === loan_id);

    params.delete("to_account_id");
    params.delete("previous");
    window.history.replaceState(
      {},
      "",
      `${document.location.href.split("?")[0]}?${params.toString()}`
    );

    if (
      !loan ||
      !loan.isValidInternalOrExternalTransferDestination(ach_allows_pull)
    ) {
      setIsInitialized(true);
      return;
    }

    if (loan.isPayableByCard() && loan_payment_by_card) {
      setToAccountId(loan_id);
    } else if (loan.isInternalLoan() && loan_principal_payment) {
      setToAccountId(loan_id);
    }

    setIsInitialized(true);
  }, [
    accounts,
    loan_payment_by_card,
    loan_principal_payment,
    ach_allows_pull,
    isWebLoanPaymentsEnabled,
  ]);

  if (!isWebLoanPaymentsEnabled) {
    if (loan_payment_by_card) {
      return (
        <LoanPaymentByCard
          limits={limits}
          loanId={toAccountId}
          goToPrevious={goToPrevious}
        />
      );
    }
    if (loan_principal_payment) {
      return (
        <LoanPaydown
          limits={limits}
          loanId={toAccountId}
          goToPrevious={goToPrevious}
        />
      );
    }

    // Neither feature enabled - show graceful empty state
    return (
      <LoanEmptyState
        titleId="loan-no-loans"
        titleFallback="Loan payments not available"
        descriptionId="loan-no-loans-description"
        descriptionFallback="You don't have any loans available for payment right now."
        onBack={goToPrevious}
      />
    );
  }

  // Wait for initialization to complete before rendering the form
  if (!isInitialized) {
    return null;
  }

  return (
    <LoanPaymentFormProvider
      initialValues={{
        ...loanPaymentFormInitialValues,
        toAccountId: toAccountId || ("" as API.AccountId),
        date: DateTime.now().toFormat("MM/dd/yyyy"),
        frequency: "ONCE",
      }}
    >
      <LoanPaymentFormComponent limits={limits} />
    </LoanPaymentFormProvider>
  );
};

export default LoanPaymentRouter;
